import React, { useEffect } from "react";
import { ensureGoogleFont, TENANT_FONTS } from "./fonts";
import { resolveTenantSlug } from "./tenant";

// Converts hex to "h s% l%" for CSS HSL
function hexToHsl(hex) {
  if (!hex) return null;
  let r = 0, g = 0, b = 0;
  const v = hex.replace("#", "");
  if (v.length === 6) {
    r = parseInt(v.slice(0, 2), 16);
    g = parseInt(v.slice(2, 4), 16);
    b = parseInt(v.slice(4, 6), 16);
  } else return null;
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function TenantTheme({ tenant }) {
  useEffect(() => {
    if (!tenant) return;
    const b = tenant.branding || {};
    const root = document.documentElement;
    const primary = hexToHsl(b.primary_color);
    const secondary = hexToHsl(b.secondary_color);
    const accent = hexToHsl(b.accent_color);
    if (primary) root.style.setProperty("--primary", primary);
    if (primary) root.style.setProperty("--ring", primary);
    if (secondary) root.style.setProperty("--secondary", secondary);
    if (accent) root.style.setProperty("--accent", accent);

    // Document title — shows in browser tab, share preview, "Add to Home Screen"
    const brandName = tenant.name || "DairyFresh";
    const tagline = b.tagline ? ` · ${b.tagline}` : "";
    document.title = `${brandName}${tagline}`;

    // Theme color (mobile browser chrome) follows the primary brand color
    if (b.primary_color) {
      let themeMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeMeta) {
        themeMeta = document.createElement("meta");
        themeMeta.name = "theme-color";
        document.head.appendChild(themeMeta);
      }
      themeMeta.setAttribute("content", b.primary_color);
    }

    // Meta description — used by browsers / link previews
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement("meta");
      descMeta.name = "description";
      document.head.appendChild(descMeta);
    }
    descMeta.setAttribute("content", b.tagline || `Order farm-fresh milk and dairy from ${brandName}`);

    // Apple PWA title (iOS "Add to Home Screen" name)
    let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleMeta) {
      appleMeta = document.createElement("meta");
      appleMeta.name = "apple-mobile-web-app-title";
      document.head.appendChild(appleMeta);
    }
    appleMeta.setAttribute("content", brandName);

    // Favicon — point to tenant logo when present
    if (b.logo_url) {
      let favicon = document.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.setAttribute("href", b.logo_url);
      // Also update apple touch icon
      let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (!appleIcon) {
        appleIcon = document.createElement("link");
        appleIcon.rel = "apple-touch-icon";
        document.head.appendChild(appleIcon);
      }
      appleIcon.setAttribute("href", b.logo_url);
    }

    // Typography: load Google Font + bind CSS var the body uses
    const fam = b.font_family || "";
    if (fam) {
      const known = TENANT_FONTS.find((f) => f.family === fam);
      ensureGoogleFont(fam, known?.weights);
      root.style.setProperty("--tenant-font", `'${fam}', ui-sans-serif, system-ui, sans-serif`);
    } else {
      root.style.removeProperty("--tenant-font");
    }
    if (b.font_weight_body) {
      root.style.setProperty("--tenant-font-weight", String(b.font_weight_body));
    } else {
      root.style.removeProperty("--tenant-font-weight");
    }

    // Dynamic per-tenant PWA manifest — replaces the static /manifest.json with
    // a data: URL containing tenant-branded name, icons and theme color. This
    // makes PWA Builder / Add-to-Home-Screen use the tenant brand on every
    // custom domain (e.g., dairyfresh.localappstore.in vs milkbuddy.localappstore.in).
    //
    // Persona detection — the same domain serves THREE distinct PWAs:
    //   * /delivery/* and /rider/*  → Rider app
    //   * /admin/* and /super/*     → Operator console
    //   * everything else           → Customer storefront
    // PWA Builder loads the URL you give it, our JS rewrites the manifest at
    // runtime, then PWA Builder serializes that manifest into the APK.
    try {
      const path = window.location.pathname;
      let persona = "customer";
      if (path.startsWith("/delivery") || path.startsWith("/rider")) persona = "rider";
      else if (path.startsWith("/admin") || path.startsWith("/super")) persona = "admin";

      const icon = b.logo_url || "/icons/icon-512.png";
      let manifest;
      if (persona === "rider") {
        const ctxSlug = resolveTenantSlug();
        const riderSlug = ctxSlug && tenant.slug && ctxSlug === tenant.slug ? ctxSlug : null;
        manifest = {
          id: "/rider-app",
          name: `${brandName} Rider`,
          short_name: "Rider",
          description: `Delivery app for ${brandName} riders`,
          scope: "/",
          // Carry THIS store in the launch URL. Without it an installed rider
          // app opens bare /delivery/login and takes its store from browser
          // storage, which every other store opened on the same origin
          // overwrites — the rider is then looked up in the wrong store.
          // Only a store the app's own context names (never the default one
          // the storefront falls back to).
          start_url: riderSlug ? `/delivery/login?t=${encodeURIComponent(riderSlug)}` : "/delivery/login",
          display: "standalone",
          orientation: "portrait",
          theme_color: "#1F2937",   // dark slate — distinct from customer
          background_color: "#0F172A",
          categories: ["business", "productivity"],
          icons: [
            { src: "/icons/rider-icon-192.png", sizes: "192x192", type: "image/png", purpose: "any" },
            { src: "/icons/rider-icon-512.png", sizes: "512x512", type: "image/png", purpose: "any" },
            { src: "/icons/rider-icon-192.png", sizes: "192x192", type: "image/png", purpose: "maskable" },
            { src: "/icons/rider-icon-512.png", sizes: "512x512", type: "image/png", purpose: "maskable" },
          ],
        };
      } else if (persona === "admin") {
        manifest = {
          id: "/admin-app",
          name: `${brandName} Admin`,
          short_name: "Admin",
          description: `Operator console for ${brandName}`,
          scope: "/",
          start_url: "/admin/login",
          display: "standalone",
          orientation: "any",
          theme_color: b.primary_color || "#3A86FF",
          background_color: "#FAF9F6",
          categories: ["business", "productivity"],
          icons: [
            { src: icon, sizes: "192x192", type: "image/png", purpose: "any" },
            { src: icon, sizes: "512x512", type: "image/png", purpose: "any" },
          ],
        };
      } else {
        manifest = {
          id: "/",
          name: brandName,
          short_name: brandName.slice(0, 12),
          description: b.tagline || `Order fresh milk & dairy from ${brandName}`,
          scope: "/",
          start_url: "/",
          display: "standalone",
          orientation: "portrait",
          theme_color: b.primary_color || "#3A86FF",
          background_color: b.background_color || "#FAF9F6",
          categories: ["food", "shopping", "lifestyle"],
          icons: [
            { src: icon, sizes: "192x192", type: "image/png", purpose: "any" },
            { src: icon, sizes: "512x512", type: "image/png", purpose: "any" },
            { src: "/icons/icon-192-maskable.png", sizes: "192x192", type: "image/png", purpose: "maskable" },
            { src: "/icons/icon-512-maskable.png", sizes: "512x512", type: "image/png", purpose: "maskable" },
          ],
        };
      }
      const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
      const manifestUrl = URL.createObjectURL(blob);
      let mlink = document.querySelector('link[rel="manifest"]');
      if (!mlink) {
        mlink = document.createElement("link");
        mlink.rel = "manifest";
        document.head.appendChild(mlink);
      }
      // Revoke the previous blob URL to avoid leaks
      const prev = mlink.getAttribute("href");
      mlink.setAttribute("href", manifestUrl);
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
    } catch {}

    return () => {
      // Reset on unmount to defaults
      root.style.removeProperty("--primary");
      root.style.removeProperty("--secondary");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--tenant-font");
      root.style.removeProperty("--tenant-font-weight");
    };
  }, [tenant]);
  return null;
}
